import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, Mail, Phone, Globe, Landmark, CreditCard, Building, Home, Calculator, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { translations } from '@/lib/translations';
import { useLanguage } from '@/hooks/useLanguage';
import ImageOptimized from '@/components/ImageOptimized';
import { ContactForm } from '@/components/ContactForm';
import { CTAButton } from '@/components/CTAButton';
import ImagePreloader from './ImagePreloader';

export function HomePage() {
  const { language } = useLanguage();
  const t = translations[language];

  const emailAddress = "info@residenciapanama.com";
  const phoneNumber = "(+507) 6958 4333";

  const handleEmailClick = () => {
    const subject = encodeURIComponent("Richiesta Informazioni");
    window.location.href = `mailto:${emailAddress}?subject=${subject}`;
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const heroImages = [
    '/images/optimized/hero/hero-1',
    '/images/optimized/hero/hero-2',
    '/images/optimized/hero/hero-3'
  ];

  const officeImages = [
    {
      url: '/images/optimized/office/office-1-desktop',
      alt: 'Reception Panama Office',
    },
    {
      url: '/images/optimized/office/office-3-desktop',
      alt: 'Professional Meeting Room Panama',
    },
  ];

  // All critical images for preloading
  const criticalImages = [
    ...heroImages,
    ...officeImages.map(img => img.url)
  ];

  useEffect(() => {
    // Preload only the first hero image for faster LCP
    const firstImage = new Image();
    firstImage.src = heroImages[0] + '.webp';
    firstImage.onload = () => setImagesLoaded(true);
    
    // Start carousel only after first image is loaded
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);
      
      return () => clearInterval(interval);
    }, 2000); // Delay carousel start

    return () => clearTimeout(timer);
  }, []);

  const services = [
    {
      icon: <Home className="w-8 h-8 text-blue-600" />,
      title: t.services.residence.title,
      description: t.services.residence.description,
      cta: t.services.residence.cta,
    },
    {
      icon: <Building className="w-8 h-8 text-blue-600" />,
      title: t.services.offshore.title,
      description: t.services.offshore.description,
      cta: t.services.offshore.cta,
    },
    {
      icon: <Landmark className="w-8 h-8 text-blue-600" />,
      title: t.services.banking.title,
      description: t.services.banking.description,
      cta: t.services.banking.cta,
    },
    {
      icon: <CreditCard className="w-8 h-8 text-blue-600" />,
      title: t.services.investment.title,
      description: t.services.investment.description,
      cta: t.services.investment.cta,
    },
    {
      icon: <Calculator className="w-8 h-8 text-blue-600" />,
      title: t.services.accounting.title,
      description: t.services.accounting.description,
      cta: t.services.accounting.cta,
    },
    {
      icon: <Scale className="w-8 h-8 text-blue-600" />,
      title: t.services.legal.title,
      description: t.services.legal.description,
      cta: t.services.legal.cta,
    },
  ];

  return (
    <>
      {/* Preload critical images for better LCP */}
      <ImagePreloader images={criticalImages} priority={true} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Images Carousel */}
        <div className="absolute inset-0">
          {/* Only render first image initially for faster LCP */}
          <div className="absolute inset-0">
            <ImageOptimized
              src={heroImages[0]}
              alt="Panama Hero"
              className="w-full h-full"
              width={1920}
              height={1080}
              priority={true}
              category="hero"
              responsive={true}
            />
          </div>
          
          {/* Render other images only after first is loaded */}
          {imagesLoaded && heroImages.slice(1).map((image, index) => (
            <div
              key={index + 1}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index + 1 === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <ImageOptimized
                src={image}
                alt={`Panama ${index + 2}`}
                className="w-full h-full"
                width={1920}
                height={1080}
                priority={false}
                category="hero"
                responsive={true}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200">
            {t.hero.subtitle}
          </p>
          <div className="flex justify-center">
                <CTAButton
                  action="consultation"
                  text={t.hero.cta}
                  variant="primary"
                  size="lg"
                  trackingData={{
                    source: 'hero-section',
                    campaign: 'main-cta',
                    service: 'consultation'
                  }}
                />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white" id="about">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">{t.about.title}</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {t.about.description}
                </p>
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600 mb-2">15+</div>
                    <div className="text-sm text-gray-600">{t.about.experience}</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                    <div className="text-sm text-gray-600">{t.about.clients}</div>
                  </div>
                </div>
                <CTAButton
                  action="contact"
                  text={t.about.cta}
                  variant="primary"
                  size="lg"
                  trackingData={{
                    source: 'about-section',
                    campaign: 'about-cta',
                    service: 'general'
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Load office images with simple img tags */}
                {officeImages.map((image, index) => (
                  <div key={index} className="relative overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={`${image.url}.webp`}
                      alt={image.alt}
                      className="w-full h-64 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        // Fallback to jpg if webp fails
                        const target = e.target as HTMLImageElement;
                        target.src = `${image.url}.jpg`;
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50" id="services">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">{t.services.title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.services.subtitle}</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <CTAButton
                    action="contact"
                    text={service.cta}
                    variant="outline"
                    fullWidth={true}
                    trackingData={{
                      source: 'services-section',
                      campaign: 'service-cta',
                      service: service.title
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-indigo-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                {t.leadMagnet.title}
              </h2>
              <p className="text-xl text-blue-100 mb-2">
                {t.leadMagnet.subtitle}
              </p>
              <p className="text-lg text-blue-200">
                ⭐ {t.leadMagnet.socialProof}
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Benefits List */}
              <div className="text-white">
                <h3 className="text-2xl font-semibold mb-6 flex items-center">
                  <span className="bg-blue-500 rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">✓</span>
                  {t.leadMagnet.benefitsTitle}
                </h3>
                <ul className="space-y-4">
                  {t.leadMagnet.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-3 h-3 bg-green-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                      <span className="text-lg">{benefit}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-8 p-4 bg-blue-800/50 rounded-lg border border-blue-500/30">
                  <p className="text-blue-100 text-sm">
                    💡 <strong>{t.leadMagnet.exclusiveBonus}</strong>
                  </p>
                </div>
              </div>
              
              {/* Lead Capture Form */}
              <div className="bg-white rounded-xl p-8 shadow-2xl">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {t.leadMagnet.form.title}
                  </h3>
                  <p className="text-gray-600">
                    {t.leadMagnet.form.description}
                  </p>
                </div>
                
                <form 
                  name="lead-capture" 
                  method="POST" 
                  data-netlify="true" 
                  data-netlify-honeypot="bot-field"
                  className="space-y-4" 
                  onSubmit={(e) => {
                    const form = e.target as HTMLFormElement;
                    const formData = new FormData(form);
                    
                    // Validate required fields
                    const name = formData.get('name') as string;
                    const email = formData.get('email') as string;
                    const interest = formData.get('interest') as string;
                    
                    if (!name?.trim()) {
                      e.preventDefault();
                      toast.error('Nome è obbligatorio');
                      return;
                    }
                    
                    if (!email?.trim() || !/\S+@\S+\.\S+/.test(email)) {
                      e.preventDefault();
                      toast.error('Email non valida');
                      return;
                    }
                    
                    if (!interest?.trim()) {
                      e.preventDefault();
                      toast.error('Seleziona il tuo interesse');
                      return;
                    }
                    
                    // Track conversion before native form submission
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'lead_generation', {
                        event_category: 'Lead Magnet',
                        event_label: 'PDF Download',
                        custom_parameters: {
                          source: 'homepage-lead-magnet',
                          content_type: 'pdf_guide',
                          interesse: interest
                        }
                      });
                    }
                    
                    // Show loading state
                    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                    submitButton.disabled = true;
                    submitButton.textContent = `⏳ ${t.leadMagnet.form.sending}`;
                    
                    // Let Netlify handle the form submission natively
                    // Don't prevent default - let it submit normally
                  }}
                >
                  {/* Hidden field for Netlify */}
                  <input type="hidden" name="form-name" value="lead-capture" />
                  
                  {/* Honeypot field for spam protection */}
                  <div style={{ display: 'none' }}>
                    <label>
                      Don't fill this out if you're human: 
                      <input name="bot-field" />
                    </label>
                  </div>
                  
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      {t.leadMagnet.form.nameLabel} *
                    </label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder={t.leadMagnet.form.namePlaceholder}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      {t.leadMagnet.form.emailLabel} *
                    </label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder={t.leadMagnet.form.emailPlaceholder}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      {t.leadMagnet.form.phoneLabel}
                    </label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder={t.leadMagnet.form.phonePlaceholder}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-1">
                      {t.leadMagnet.form.interestLabel} *
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Seleziona il tuo interesse</option>
                      <option value="residenza">Residenza Permanente</option>
                      <option value="business">Aprire un Business</option>
                      <option value="investimenti">Investimenti Immobiliari</option>
                      <option value="fiscale">Ottimizzazione Fiscale</option>
                      <option value="pensione">Pensione a Panama</option>
                    </select>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    {t.leadMagnet.form.submitButton}
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center mt-3">
                    🔒 {t.leadMagnet.form.privacyText}
                  </p>
                </form>
                
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {t.leadMagnet.form.features.immediate}
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {t.leadMagnet.form.features.free}
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {t.leadMagnet.form.features.noSpam}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-blue-900 text-white" id="contact">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">{t.contact.title}</h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">{t.contact.subtitle}</p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Info */}
              <div>
                <h3 className="text-2xl font-semibold mb-8">{t.contact.info.title}</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Building2 className="w-6 h-6 text-blue-300 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">{t.contact.office}</h4>
                      <p className="text-blue-100">Avenida 10 Norte Calle Tael Casa C1 Urbanizacion Los Angeles Panama City</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Phone className="w-6 h-6 text-blue-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">{t.contact.phone}</h4>
                      <a href={`tel:${phoneNumber}`} className="text-blue-100 hover:text-white transition-colors">
                        {phoneNumber}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Mail className="w-6 h-6 text-blue-300 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">{t.contact.email}</h4>
                      <button 
                        onClick={handleEmailClick}
                        className="text-blue-100 hover:text-white transition-colors"
                      >
                        {emailAddress}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Globe className="w-6 h-6 text-blue-300 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold mb-1">{t.contact.website}</h4>
                      <a 
                        href="https://rsinternationalservices.com/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-100 hover:text-white transition-colors"
                      >
                        rsinternationalservices.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-2xl font-semibold mb-8">{t.contact.form.title}</h3>
                <ContactForm 
                  source="homepage-contact" 
                  service="general-inquiry"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <div className="mb-6">
                  <h3 className="text-xl font-bold">
                    <a href="https://residenciapanama.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">
                      ResidenciaPanama
                    </a>
                  </h3>
                  <p className="text-gray-400">
                    è un servizio di{' '}
                    <a href="https://rsinternationalservices.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">
                      RS International Services
                    </a>
                  </p>
                </div>
                <p className="text-gray-400 mb-6 max-w-md">
                  {t.footer.description}
                </p>
                <div className="flex space-x-4">
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <Mail className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <Phone className="w-5 h-5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <Globe className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">{t.nav.title}</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="hover:text-blue-400">{t.nav.home}</a></li>
                  <li><a href="#about" className="hover:text-blue-400">{t.nav.about}</a></li>
                  <li><a href="#services" className="hover:text-blue-400">{t.nav.services}</a></li>
                  <li><a href="#contact" className="hover:text-blue-400">{t.nav.contact}</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">{t.services.title}</h3>
                <ul className="space-y-2">
                  <li><a href="#services" className="hover:text-blue-400">{t.services.residence.title}</a></li>
                  <li><a href="#services" className="hover:text-blue-400">{t.services.offshore.title}</a></li>
                  <li><a href="#services" className="hover:text-blue-400">{t.services.banking.title}</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
              <p>&copy; 2024 RS International Services. {t.footer.rights}</p>
              <p>
                <a href="https://rstech.services/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">Powered by Rs Tech Services</a>
                {' | '}
                <a href="https://p3consulting.tech" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">Technology by P3 Consulting</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}